/* @flow */
/* eslint-env es6,browser */

//set global variable needed for A9 library
var _amznads = window.amznads || {};
window.amznads = _amznads;

import * as u from '../utils';

/**
 * @description Implements functionality for amazon (a9) header bidding
 * @exports bidders/a9bid
 * @requires {@link module:utils}
 */
class _a9bid {
  _name: string;
  _bInited: 1 | 0 | -1;
  _bFirst: boolean;
  _DefConfig: a9Config;
  _config: a9Config;
  _excludeList: Array<string>;

  //Member variable default initialization in constructor
  constructor(){
    this._name = 'A9';
    this._bInited = u.F_NOT_INITED;
    this._bFirst = true;
    this._DefConfig = u.extend(true, this._DefConfig, {
      positions: [],
      exclude: '',
      timeout: 2000,
      initTimeout: 1000,
      script: '',
      id: '',
      qpKey: 'AMZ_BID',
      cVar: 'AMZ_KEY',
      posTokenMap: {}
    });

    /**
     * @global
     * @typedef a9Config
     * @property {Array<string>} positions
     * @property {string} [exclude]
     * @property {number} [timeout=2000]
     * @property {string} [cVar=AMZ_KEY]
     * @property {Object} posTokenMap
     * @property {string} script
     * @property {string} id
     * @property {number} [initTimeout=1000]
     */
    this._config = u.extend(true, this._DefConfig);
    this._excludeList = [];
  }

  /**
   * get list of bid positions supported by this header bidder
   * @param {string} pName
   * @return {Array<string>}
   */
  getBidPositions(pName: string): Array<string> {
    var ret = this._config.positions;
    if(u.isBrowserExcluded(pName, this._excludeList)) {
      ret = [];
    }
    return ret;
  }

  /**
   * Reset & Update config specific to this bidder
   * @param {a9Config} oConfig
   *
   */
  updateConfig(oConfig: a9Config) {
    //$FlowFixMe
    this._config = {};
    u.extend(true, this._config, this._DefConfig, oConfig);
    //compute exclusion list again
    this._excludeList = u.getBrowserExcludeList(this._config.exclude);
  }

  /**
   * getter for qpkey
   * @param {string} pos
   */
  getQPKey(/*pos*/) {
    return this._config.qpKey;
  }

  /**
   * getter for posKey
   * @param {string} pos
   */
  getPosKey(pos: string): string {
    return this._config.posTokenMap[pos];
  }

  /**
   * getter for cVar
   * @param {string} pos
   */
  getCVar(): string {
    return this._config.cVar;
  }

  /**
   * We need to init/load Amazon JS library only once
   * Current promise will wait for the timeout. During next invocation
   * if the script is not loaded (by checking AMZN defined fn), we consider
   * script loading failed. Ad blocking/AMZN server issue? We should silently skip header
   * bidding in this case
   * @access private
   */
  _init(/**posList */) {
    //returns promise
    return new Promise((resolve, reject) => {
      if(this._bInited === u.F_INITED) {
        if(!u.isObject(_amznads) || !u.isFunction(_amznads.getAdsCallback)) {
          this._bInited = u.F_FAILED;
          reject();
        } else {
          resolve();
        }
        return;
      }else if(this._bInited === u.F_FAILED) {
        reject();
        return;
      }
      //Let's load the config for the first time
      //Let's do a basic santiy check (script shdn't be empty etc.,)
      if(!this._config.script.length|| !this._config.id.length) {
        u.logError(`${this._name} config missing mandatory data`);
        reject();
        return;
      }

      var misPos = this._config.positions.filter(pos => !this._config.posTokenMap[pos]);
      if (misPos.length) {
        u.logError(`${this._name} config positions missing in posTokenMap ${misPos.join(',')}`);
        reject();
        return;
      }

      //configure and load AMZN script
      _amznads.asyncParams = {
        id: this._config.id,
        timeout: this._config.timeout,
        callbackFn: () => {
          u.logDebug(`${this._name} on load callback`);
          //AMZN library loaded successfully.
          try {
            resolve();
          }catch(e){reject();}
        }
      };

      //load amazon script here
      u.loadJSWithTimeout(this._config.script, document, true, this._config.initTimeout,
                          { context: window.amznads, func: 'getAdsCallback' }).then(() => resolve(),
                                                                                    () => reject());
      this._bInited = u.F_INITED;
    });
  }

  /**
   * AMZN tokens will have positions<sep>price_point. Split and get information
   * @access private
   * */
  _processTokens(posList: Array<string>, tokens: Array<string>, ret: hbPartnerData): hbPartnerData {
    var rx = /t|p/, tokenMap = {}, a, res = {}, prefix = '', slot = 0;

    //Build object map here to return necessary values
    //This ensures that unique & highest bidding(slot)
    u.logDebug(`${this._name} processing tokens ${tokens.join(',')}`);
    tokens.forEach(token => {
      a = token.split(rx);
      if (a.length !== 2) {
        return;
      }
      prefix = a[0];
      slot = parseInt(a[1], 10);

      //if we get multiple buckets for the same position, use the lowest one
      if (!u.isObject(tokenMap[prefix]) || (slot < tokenMap[prefix].slot)) {
        tokenMap[prefix] = {
          slot: slot,
          token: token
        };
      }
    });

    posList.forEach(pos => {
      prefix = this.getPosKey(pos);
      res = tokenMap[prefix];
      if (res) {
        u.logDebug(`${this._name} id:${prefix} res.slot:${res.slot} token:${res.token}`);
        ret[pos].info = `${prefix}|${slot}`;

        //query param string to pass to OpenX ad call
        ret[pos]._QP[this.getQPKey()] = res.token;

        //AMZN ad key is stored as customVar for OpenX ad to use
        ret[pos]._CV[this._config.cVar] = res.token;
      }
    });

    return ret;
  }

  _getAdsCallback(posList: Array<string>, resolve: Function, ret: hbPartnerData) {
    //Give sometime for A9 to execute the JSONP callback
    //only then data will be available with us
    setTimeout(() => {
      var tok = _amznads.getTokens();
      ret = this._processTokens(posList, tok, ret);
      resolve(ret);
    }, 0);
  }

  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<*> {
    posList.forEach(pos => ret[pos]._QP[this.getQPKey()] = `${this.getPosKey(pos)}p0`);
    return new Promise(resolve => {
      if(this._bFirst) {
        //We already got some bidding when we load the AMZN library first
        //Let's use it. No need to send request again
        this._bFirst = false;
        this._getAdsCallback(posList, resolve, ret);
        return;
      }
      //Not the first time, let's send request now
      _amznads.getAdsCallback(this._config.id, () => {
        this._getAdsCallback(posList, resolve, ret);
      });
      return;
    });
  }

  /**
   * get bids for current partner
   * @param {Array<string>} posList - list of ad positions
   * @param {Object.<string, hbPosData>} ret - header bidding default response
   * @return {Promise<Object.<string, hbPosData>>}
   */
  getBids(posList: Array<string>, ret: hbPartnerData): Promise<*> {
    return this._init(posList).then(() => {
      //get bids
      u.logDebug(`${this._name} Init is done. getting ad bids`);
      return this._getAds(posList, ret);
    }, () => {
      u.logError(`${this._name} init failed`);
      return ret;
    });
  }

 /**
  * render ad for this partner
  * @param {window} win - window object
  * @param {window.document} doc - document object
  * @param {string} pos - ad position
  * @param {string} token - token for this position
  */
  renderAd(win: window, doc: window.document, pos: string, token: string) {
    try {
      _amznads.renderAd(doc, token);
    } catch(e) {
      u.logDebug(`${this._name} render ad failure ${e.message}`);
    }
  }
}
//export singleton object as default for this module
export default new _a9bid();
